import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import type { DealAnalyzerAnalysis } from '../../types/dealAnalyzer';
import { SITE_GLASS_CARD } from '../../constants/siteUi';

function fmtUsd(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtUsd0(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtPct(n: number | null | undefined, digits = 2): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(digits)}%`;
}

function irrCellBg(irr: number): string {
  const t = Math.max(0, Math.min(1, (irr + 15) / 35));
  const h = Math.round(12 + t * 88);
  const s = 58;
  const l = Math.round(22 + t * 18);
  return `hsl(${h} ${s}% ${l}%)`;
}

function toneClass(tone: string | undefined): string {
  if (tone === 'positive') return 'text-emerald-300';
  if (tone === 'negative') return 'text-rose-300';
  return 'text-[var(--home-text-on-dark)]';
}

const DONUT_COLORS = ['#38bdf8', '#34d399', '#a78bfa', '#f472b6', '#fbbf24'];

export function DealAnalyzerResults({ analysis }: { analysis: DealAnalyzerAnalysis }) {
  const [extractedOpen, setExtractedOpen] = useState(true);
  const [chartsHidden, setChartsHidden] = useState(false);

  const incomeCount = analysis.extractedLineItems?.income?.length ?? 0;
  const expCount = analysis.extractedLineItems?.operatingExpenses?.length ?? 0;
  const extractedCount = incomeCount + expCount;

  const lineChartData = useMemo(() => {
    const years = analysis.projections?.years ?? [];
    const noi = analysis.projections?.noi ?? [];
    const debt = analysis.projections?.debtService ?? [];
    const cf = analysis.projections?.cashFlow ?? [];
    return years.map((y, i) => ({
      year: `Y${y}`,
      NOI: noi[i] ?? 0,
      'Debt service': debt[i] ?? 0,
      'Cash flow': cf[i] ?? 0,
    }));
  }, [analysis.projections]);

  const cocData = useMemo(() => {
    const years = analysis.projections?.years ?? [];
    const coc = analysis.projections?.cashOnCashPct ?? [];
    return years.map((y, i) => ({ year: `Y${y}`, coc: coc[i] ?? 0 }));
  }, [analysis.projections]);

  const equityChartData = useMemo(() => {
    const years = analysis.projections?.years ?? [];
    const eq = analysis.projections?.totalEquity ?? [];
    return years.map((y, i) => ({ year: `Y${y}`, equity: eq[i] ?? 0 }));
  }, [analysis.projections]);

  const acquisitionData = useMemo(
    () =>
      (analysis.projections?.acquisitionDonut ?? []).map((d, i) => ({
        name: d.label,
        value: d.amount,
        pct: d.pct,
        fill: DONUT_COLORS[i % DONUT_COLORS.length],
      })),
    [analysis.projections?.acquisitionDonut]
  );

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-2 border-b border-white/[0.08] pb-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[var(--home-miami-blue)]">
            Analysis results
          </p>
          <h2 className="font-display text-xl font-bold text-[var(--home-text-on-dark)] md:text-2xl">
            Comprehensive Deal Analyzer
          </h2>
          <p className="font-body text-sm text-[var(--home-muted-on-dark)]">Investment analysis — AI-assisted underwriting</p>
        </div>
      </header>

      {analysis.extractedLineItems &&
      (analysis.extractedLineItems.income?.length || analysis.extractedLineItems.operatingExpenses?.length) ? (
        <div className={`${SITE_GLASS_CARD} overflow-hidden p-0`}>
          <button
            type="button"
            onClick={() => setExtractedOpen((v) => !v)}
            className="flex w-full items-center justify-between border-b border-white/[0.08] bg-[var(--home-miami-blue)]/15 px-4 py-3 text-left font-display text-xs font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)] md:px-6"
          >
            <span>Extracted financial data ({extractedCount})</span>
            <span className="text-[var(--home-miami-blue)]">{extractedOpen ? '▼' : '▶'}</span>
          </button>
          {extractedOpen ? (
            <div className="grid gap-6 p-4 font-body text-sm md:grid-cols-2 md:p-6">
              <div>
                <p className="mb-3 font-mono text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
                  Income
                </p>
                <ul className="space-y-2">
                  {analysis.extractedLineItems.income?.map((row) => (
                    <li key={row.label} className="flex justify-between gap-4 border-b border-white/[0.06] py-2">
                      <span className="text-[var(--home-text-on-dark)]/90">{row.label}</span>
                      <span className="shrink-0 font-medium tabular-nums text-[var(--home-text-on-dark)]">
                        {fmtUsd(row.annual)}/yr
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="mb-3 font-mono text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
                  Operating expenses
                </p>
                <ul className="space-y-2">
                  {analysis.extractedLineItems.operatingExpenses?.map((row) => (
                    <li key={row.label} className="flex justify-between gap-4 border-b border-white/[0.06] py-2">
                      <span className="text-[var(--home-text-on-dark)]/90">{row.label}</span>
                      <span className="shrink-0 font-medium tabular-nums text-[var(--home-text-on-dark)]">
                        {fmtUsd(row.annual)}/yr
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {analysis.kpis && analysis.kpis.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {analysis.kpis.map((k) => (
            <div key={k.key ?? k.label} className={`${SITE_GLASS_CARD} border-white/[0.06] p-4`}>
              <p className="font-mono text-[0.55rem] font-medium uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                {k.label}
              </p>
              <p className={`mt-2 font-display text-lg font-semibold tabular-nums ${toneClass(k.tone)}`}>{k.value}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className={`${SITE_GLASS_CARD} space-y-4 p-5 md:p-6`}>
          <h3 className="font-display text-xs font-bold uppercase tracking-[0.14em] text-[var(--home-text-on-dark)]">
            Property description
          </h3>
          <dl className="grid gap-2 font-body text-sm text-[var(--home-text-on-dark)]">
            <div className="flex justify-between gap-4 border-b border-white/[0.06] py-2">
              <dt className="text-[var(--home-muted-on-dark)]">Address</dt>
              <dd className="text-right">{analysis.property?.address ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-white/[0.06] py-2">
              <dt className="text-[var(--home-muted-on-dark)]">Year built</dt>
              <dd className="tabular-nums">{analysis.property?.yearBuilt ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-white/[0.06] py-2">
              <dt className="text-[var(--home-muted-on-dark)]">Units</dt>
              <dd className="tabular-nums">{analysis.property?.units ?? '—'}</dd>
            </div>
          </dl>
          {analysis.property?.unitMix && analysis.property.unitMix.length > 0 ? (
            <div>
              <p className="mb-2 font-mono text-[0.55rem] uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                Unit mix
              </p>
              <ul className="space-y-1.5 text-sm">
                {analysis.property.unitMix.map((u) => (
                  <li key={u.label} className="flex justify-between gap-2 border-b border-white/[0.05] py-1.5">
                    <span>
                      {u.label} × {u.count}
                    </span>
                    <span className="tabular-nums text-[var(--home-muted-on-dark)]">
                      {u.sqft != null ? `${u.sqft.toLocaleString()} sf` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-4 border-t border-white/[0.06] pt-4 text-sm">
            <div>
              <p className="text-[0.65rem] uppercase tracking-wide text-[var(--home-muted-on-dark)]">Total SF</p>
              <p className="font-semibold tabular-nums">{analysis.property?.totalSqft?.toLocaleString() ?? '—'}</p>
            </div>
            <div>
              <p className="text-[0.65rem] uppercase tracking-wide text-[var(--home-muted-on-dark)]">Avg rent / mo</p>
              <p className="font-semibold tabular-nums">{fmtUsd(analysis.property?.avgRentPerMonth ?? null)}</p>
            </div>
          </div>
          {analysis.property?.sourceFile ? (
            <p className="text-xs italic text-[var(--home-muted-on-dark)]">Source: {analysis.property.sourceFile}</p>
          ) : null}
        </div>

        <div className={`${SITE_GLASS_CARD} overflow-hidden p-0`}>
          <div className="border-b border-white/[0.08] bg-[var(--home-miami-blue)]/12 px-4 py-3 md:px-6">
            <h3 className="font-display text-xs font-bold uppercase tracking-[0.14em]">Cash flow summary</h3>
          </div>
          <div className="overflow-x-auto p-4 md:p-6">
            <table className="w-full min-w-[320px] text-left font-body text-sm">
              <thead>
                <tr className="font-mono text-[0.55rem] uppercase tracking-[0.1em] text-[var(--home-muted-on-dark)]">
                  <th className="pb-2 pr-4">Item</th>
                  <th className="pb-2 pr-4 text-right">Annual</th>
                  <th className="pb-2 text-right">$/SF/yr</th>
                </tr>
              </thead>
              <tbody>
                {analysis.cashFlowSummary?.rows?.map((row) => {
                  const neg = row.annual < 0 || row.kind === 'deduction';
                  const bold = row.kind === 'subtotal' || row.kind === 'total';
                  const green = row.kind === 'total' && row.label.toLowerCase().includes('cash flow');
                  return (
                    <tr key={row.label} className="border-t border-white/[0.06]">
                      <td className={`py-2 pr-4 ${bold ? 'font-semibold' : ''}`}>{row.label}</td>
                      <td
                        className={`py-2 pr-4 text-right tabular-nums ${bold ? 'font-semibold' : ''} ${neg ? 'text-rose-300' : ''} ${green ? 'text-emerald-300' : ''}`}
                      >
                        {fmtUsd(row.annual)}
                      </td>
                      <td
                        className={`py-2 text-right tabular-nums text-[var(--home-muted-on-dark)] ${bold ? 'font-medium text-[var(--home-text-on-dark)]' : ''}`}
                      >
                        {row.perSfYr != null ? fmtUsd(row.perSfYr) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className={`${SITE_GLASS_CARD} space-y-4 p-5 md:p-6`}>
          <h3 className="font-display text-xs font-bold uppercase tracking-[0.14em]">
            Exit analysis (year {analysis.assumptions?.holdPeriodYrs ?? 5} disposition)
          </h3>
          <dl className="space-y-2 font-body text-sm">
            <div className="flex justify-between border-b border-white/[0.06] py-2">
              <dt className="text-[var(--home-muted-on-dark)]">Year-5 NOI</dt>
              <dd className="tabular-nums">{fmtUsd(analysis.exitAnalysis?.year5Noi)}</dd>
            </div>
            <div className="flex justify-between border-b border-white/[0.06] py-2">
              <dt className="text-[var(--home-muted-on-dark)]">Exit cap</dt>
              <dd className="tabular-nums">{fmtPct(analysis.exitAnalysis?.exitCapRatePct)}</dd>
            </div>
            <div className="flex justify-between border-b border-white/[0.06] py-2">
              <dt className="text-[var(--home-muted-on-dark)]">Gross exit price</dt>
              <dd className="tabular-nums font-semibold">{fmtUsd(analysis.exitAnalysis?.grossExitPrice)}</dd>
            </div>
            <div className="flex justify-between border-b border-white/[0.06] py-2">
              <dt className="text-[var(--home-muted-on-dark)]">Exit price / unit</dt>
              <dd className="tabular-nums">{fmtUsd(analysis.exitAnalysis?.exitPricePerUnit)}</dd>
            </div>
          </dl>
          {analysis.exitAnalysis?.deductions?.map((d) => (
            <div key={d.label} className="flex justify-between text-sm text-rose-300">
              <span>{d.label}</span>
              <span className="tabular-nums">{fmtUsd(d.amount)}</span>
            </div>
          ))}
          <div className="border-t border-white/[0.08] pt-3 text-sm">
            <div className="flex justify-between font-semibold">
              <span>Net sale proceeds</span>
              <span className="tabular-nums">{fmtUsd(analysis.exitAnalysis?.netSaleProceeds)}</span>
            </div>
            <div className="mt-2 flex justify-between text-[var(--home-muted-on-dark)]">
              <span>Principal repaid over hold</span>
              <span className="tabular-nums text-[var(--home-text-on-dark)]">
                {fmtUsd(analysis.exitAnalysis?.principalRepaidOverHold)}
              </span>
            </div>
            <div className="mt-2 flex justify-between font-semibold text-emerald-300">
              <span>Net equity proceeds</span>
              <span className="tabular-nums">{fmtUsd(analysis.exitAnalysis?.netEquityProceeds)}</span>
            </div>
          </div>
        </div>

        <div className={`${SITE_GLASS_CARD} space-y-4 p-5 md:p-6`}>
          <h3 className="font-display text-xs font-bold uppercase tracking-[0.14em]">Returns summary</h3>
          <dl className="space-y-2 font-body text-sm">
            {[
              ['Total equity invested', fmtUsd(analysis.returnsSummary?.totalEquityInvested)],
              ['Going-in cap rate', fmtPct(analysis.returnsSummary?.goingInCapRatePct)],
              ['Avg annual cash-on-cash', fmtPct(analysis.returnsSummary?.avgAnnualCashOnCashPct)],
              ['5-year equity_multiple', `${analysis.returnsSummary?.fiveYearEquityMultiple?.toFixed(2) ?? '—'}x`],
              ['5-year IRR', fmtPct(analysis.returnsSummary?.fiveYearIrrPct)],
              ['5-year total cash flow', fmtUsd(analysis.returnsSummary?.fiveYearTotalCashFlow)],
              ['Net equity at exit', fmtUsd(analysis.returnsSummary?.netEquityAtExit)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-white/[0.06] py-2">
                <dt className="text-[var(--home-muted-on-dark)]">{k}</dt>
                <dd className="tabular-nums font-medium">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {analysis.purchaseInvestment && analysis.purchaseInvestment.length > 0 ? (
        <div className={`${SITE_GLASS_CARD} overflow-hidden p-0`}>
          <div className="border-b border-white/[0.08] bg-[var(--home-miami-blue)]/12 px-4 py-3 md:px-6">
            <h3 className="font-display text-xs font-bold uppercase tracking-[0.14em]">Purchase &amp; investment</h3>
          </div>
          <div className="overflow-x-auto p-4 md:p-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[0.55rem] uppercase text-[var(--home-muted-on-dark)]">
                  <th className="pb-2 text-left">Item</th>
                  <th className="pb-2 text-right">Amount</th>
                  <th className="pb-2 text-right">$/SF</th>
                </tr>
              </thead>
              <tbody>
                {analysis.purchaseInvestment.map((r) => (
                  <tr key={r.item} className="border-t border-white/[0.06]">
                    <td className={`py-2 ${r.bold ? 'font-semibold' : ''}`}>{r.item}</td>
                    <td className={`py-2 text-right tabular-nums ${r.bold ? 'font-semibold' : ''}`}>{fmtUsd(r.amount)}</td>
                    <td className="py-2 text-right tabular-nums text-[var(--home-muted-on-dark)]">
                      {r.perSf != null ? fmtUsd(r.perSf) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {analysis.sensitivity?.irrGrid &&
      analysis.sensitivity.entryCapRates &&
      analysis.sensitivity.exitCapLabels &&
      analysis.sensitivity.irrGrid.length > 0 ? (
        <div className={`${SITE_GLASS_CARD} overflow-hidden p-0`}>
          <div className="border-b border-white/[0.08] px-4 py-3 md:px-6">
            <h3 className="font-display text-xs font-bold uppercase tracking-[0.14em]">
              Return sensitivity — levered IRR vs caps
            </h3>
            <p className="mt-1 text-xs text-[var(--home-muted-on-dark)]">
              Highlighted cell = baseline assumptions (entry × exit). Entry caps imply different purchase prices (NOI ÷ cap).
            </p>
          </div>
          <div className="overflow-x-auto p-4 md:p-6">
            <table className="min-w-[520px] border-collapse text-center font-body text-xs">
              <thead>
                <tr>
                  <th className="p-2 text-left text-[var(--home-muted-on-dark)]">Exit \ Entry</th>
                  {analysis.sensitivity.entryCapRates.map((c) => (
                    <th key={c} className="p-2 font-mono text-[0.55rem] text-[var(--home-muted-on-dark)]">
                      {c.toFixed(2)}%
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {analysis.sensitivity.irrGrid.map((row, ri) => (
                  <tr key={ri}>
                    <td className="p-2 text-left font-mono text-[0.6rem] text-[var(--home-text-on-dark)]">
                      {analysis.sensitivity!.exitCapLabels![ri] ?? `Exit ${ri}`}
                    </td>
                    {row.map((irr, ci) => {
                      const hi =
                        ri === analysis.sensitivity?.highlightExitIdx &&
                        ci === analysis.sensitivity?.highlightEntryIdx;
                      return (
                        <td
                          key={ci}
                          className={`p-2 font-mono tabular-nums ${hi ? 'ring-2 ring-[var(--home-miami-blue)] ring-inset' : ''}`}
                          style={{ backgroundColor: irrCellBg(irr) }}
                        >
                          {irr.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {analysis.projections?.years && analysis.projections.years.length > 0 ? (
        <div className={`${SITE_GLASS_CARD} space-y-4 p-4 md:p-6`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-xs font-bold uppercase tracking-[0.14em]">Financial projections</h3>
            <button
              type="button"
              onClick={() => setChartsHidden((v) => !v)}
              className="rounded-lg border border-white/15 px-3 py-1.5 font-body text-xs text-[var(--home-miami-blue)] hover:bg-white/5"
            >
              {chartsHidden ? 'Show charts' : 'Hide charts'}
            </button>
          </div>
          {!chartsHidden ? (
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="h-[280px]">
                <p className="mb-2 text-center font-mono text-[0.55rem] uppercase text-[var(--home-muted-on-dark)]">
                  Cash flow (NOI, debt, CF)
                </p>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff22" />
                    <XAxis dataKey="year" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ background: '#0f172a', border: '1px solid #334155' }}
                      formatter={(v) => fmtUsd0(typeof v === 'number' ? v : Number(v))}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="NOI" stroke="#38bdf8" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="Debt service" stroke="#f87171" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="Cash flow" stroke="#34d399" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="h-[280px]">
                <p className="mb-2 text-center font-mono text-[0.55rem] uppercase text-[var(--home-muted-on-dark)]">
                  Total equity
                </p>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={equityChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff22" />
                    <XAxis dataKey="year" stroke="#94a3b8" strokeWidth={1} fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `$${(v / 1e6).toFixed(2)}M`} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155' }} />
                    <Line type="monotone" dataKey="equity" stroke="#a78bfa" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="h-[280px]">
                <p className="mb-2 text-center font-mono text-[0.55rem] uppercase text-[var(--home-muted-on-dark)]">
                  Cash-on-cash
                </p>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cocData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff22" />
                    <XAxis dataKey="year" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} unit="%" />
                    <Tooltip
                      formatter={(v) =>
                        `${(typeof v === 'number' ? v : Number(v)).toFixed(2)}%`
                      }
                    />
                    <Bar dataKey="coc" fill="#a78bfa" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="h-[280px]">
                <p className="mb-2 text-center font-mono text-[0.55rem] uppercase text-[var(--home-muted-on-dark)]">
                  Acquisition uses
                </p>
                {acquisitionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={acquisitionData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={56}
                        outerRadius={88}
                        paddingAngle={2}
                      >
                        {acquisitionData.map((_, i) => (
                          <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmtUsd(typeof v === 'number' ? v : Number(v))} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="flex h-full items-center justify-center text-sm text-[var(--home-muted-on-dark)]">
                    No acquisition breakdown provided
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {analysis.normalizationReportMarkdown ? (
        <div className={`${SITE_GLASS_CARD} overflow-hidden p-0`}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/[0.08] bg-[var(--home-miami-blue)]/15 px-4 py-3 md:px-6">
            <h3 className="font-display text-xs font-bold uppercase tracking-[0.14em] text-[var(--home-text-on-dark)]">
              AI normalization report
            </h3>
          </div>
          <div
            className="max-w-none space-y-4 p-5 font-body text-sm leading-relaxed text-[var(--home-text-on-dark)]/95 [&_a]:font-medium [&_a]:text-[var(--home-miami-blue)] hover:[&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-white/15 [&_blockquote]:pl-4 [&_blockquote]:text-[var(--home-muted-on-dark)] [&_code]:rounded [&_code]:bg-white/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em] [&_h1]:mb-3 [&_h1]:mt-6 [&_h1]:font-display [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-[var(--home-text-on-dark)] [&_h1]:first:mt-0 [&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:font-display [&_h2]:text-base [&_h2]:font-bold [&_h2]:uppercase [&_h2]:tracking-wide [&_h2]:text-[var(--home-text-on-dark)] [&_hr]:my-6 [&_hr]:border-white/10 [&_li]:marker:text-[var(--home-miami-blue)] [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5 [&_p]:text-[var(--home-text-on-dark)]/90 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:border [&_pre]:border-white/10 [&_pre]:bg-black/30 [&_pre]:p-4 [&_pre]:font-mono [&_pre]:text-xs [&_strong]:font-semibold [&_strong]:text-[var(--home-text-on-dark)] [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs [&_td]:border [&_td]:border-white/10 [&_td]:px-2 [&_td]:py-1.5 [&_th]:border [&_th]:border-white/10 [&_th]:bg-white/5 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5 md:p-8"
          >
            <ReactMarkdown>{analysis.normalizationReportMarkdown}</ReactMarkdown>
          </div>
        </div>
      ) : null}
    </div>
  );
}
